import { List, Set } from "immutable";
import { pull } from "lodash";

import { SYNC_NARRATIVE_FOLDER_IDENTIFIER } from "../../constants/syncNarrativeFolderIdentifier";
import getIdsInTree from "../../utils/tree/getIdsInTree";
import getSubTreeWithId from "../../utils/tree/getSubTreeWithId";
import replaceSubTree from "../../utils/tree/replaceSubTree";
import getEntityTypeForDocument from "./getEntityTypeForDocument";
import isIgnorableSyncType from "./isIgnorableSyncType";

const removeAutoIgnoreChildren = (
  tree,
  documents,
  rootFolderId,
  depthBelowRoot,
  syncSettings,
  parentType,
  typesById,
  manualIgnoreIds,
  existingItemTypesByid
) => {
  let ignoredIds = [];

  const folderSettings = syncSettings.entityFolders.get(rootFolderId);
  const isFlattened = folderSettings.flattened;

  const updatedTree = tree.update("children", children => {
    return children
      .map(subtree => {
        const doc = documents.get(subtree.id);
        // First work out what type this document is
        const itemType = existingItemTypesByid.has(subtree.id)
          ? existingItemTypesByid.get(subtree.id)
          : getEntityTypeForDocument(syncSettings, doc.type, rootFolderId, depthBelowRoot);

        // And then work out if we are going to ignore this item
        const ignoreItem = Boolean(
          !typesById.has(itemType) || (parentType && !typesById.get(itemType).superTypes.contains(parentType))
        );

        // If it will be ignored, add all of its child ids to our ignored list and return null so we can clear it with the
        // next filter.
        if (ignoreItem || (isFlattened && depthBelowRoot > 0)) {
          const subtreeIds = getIdsInTree(subtree);
          ignoredIds.push(...subtreeIds);
          return null;
        }

        // If this item is manually ignored, we don't ignore this item, but we do
        // ignore its children
        if (manualIgnoreIds.has(subtree.id)) {
          const subtreeIds = getIdsInTree(subtree);
          pull(subtreeIds, subtree.id);
          ignoredIds.push(...subtreeIds);
          return subtree.set("children", List());
        }

        // If we reach here, then we will be keeping this item, so call recursively for its children
        const { tree: childTree, ignoredIds: childIgnoredIds } = removeAutoIgnoreChildren(
          subtree,
          documents,
          rootFolderId,
          depthBelowRoot + 1,
          syncSettings,
          itemType,
          typesById,
          manualIgnoreIds,
          existingItemTypesByid
        );
        ignoredIds.push(...childIgnoredIds);
        return childTree;
      })
      .filter(d => d);
  });

  return {
    tree: updatedTree,
    ignoredIds
  };
};

const removeAutoIgnoreNarrativeChildren = (tree, documents, syncSettings, manualIgnoreIds) => {
  let ignoredIds = [];

  const updatedTree = tree.update("children", children => {
    return children
      .map(subtree => {
        const doc = documents.get(subtree.id);
        // First work out what type this document is
        const ignoreItem = isIgnorableSyncType(doc.type);

        // If it will be ignored, add all of its child ids to our ignored list and return null so we can clear it with the
        // next filter.
        if (ignoreItem) {
          const subtreeIds = getIdsInTree(subtree);
          ignoredIds.push(...subtreeIds);
          return null;
        }

        // If this item is manually ignored, we don't ignore this item, but we do
        // ignore its children
        if (manualIgnoreIds.has(subtree.id)) {
          let subtreeIds = getIdsInTree(subtree);
          pull(subtreeIds, subtree.id);
          ignoredIds.push(...subtreeIds);
          return subtree.set("children", List());
        }

        // If we reach here, then we will be keeping this item, so call recursively for its children
        const { tree: childTree, ignoredIds: childIgnoredIds } = removeAutoIgnoreNarrativeChildren(
          subtree,
          documents,
          syncSettings,
          manualIgnoreIds
        );
        ignoredIds.push(...childIgnoredIds);
        return childTree;
      })
      .filter(d => d);
  });

  return {
    tree: updatedTree,
    ignoredIds
  };
};

/**
 * @param {SyncRepresentationRecord} rep
 * @param {SyncSettingsRecord} syncSettings
 * @param {Map} typesById
 * @param {Set} manualIgnoreIds
 * @param {Map} existingItemTypesByid
 */
export default (rep, syncSettings, typesById, manualIgnoreIds, existingItemTypesByid = Map()) => {
  if (!rep) {
    return rep;
  }

  let autoIgnoredRep = rep;
  const folderIds = syncSettings.entityFolders
    .map(f => f.folderId)
    .filter(f => f)
    .toList();

  const documentTree = rep.documentTree;
  let allIgnoredIds = [];

  // We need to go through the trees for each folder
  // and work out which documents/folders within that root to
  // automatically ignore
  folderIds.forEach(folderId => {
    const subtree = getSubTreeWithId(documentTree, folderId);
    if (subtree) {
      const { tree, ignoredIds } = removeAutoIgnoreChildren(
        subtree,
        rep.documents,
        folderId,
        0,
        syncSettings,
        null,
        typesById,
        manualIgnoreIds,
        existingItemTypesByid
      );

      // Replace our subtree in rep
      autoIgnoredRep = autoIgnoredRep.update("documentTree", documentTree => replaceSubTree(documentTree, tree));
      // Add ignored IDs into our list
      allIgnoredIds.push(...ignoredIds);
    }
  });

  // And repeat for Narrative
  const narrativeTree = getSubTreeWithId(documentTree, SYNC_NARRATIVE_FOLDER_IDENTIFIER);
  if (narrativeTree) {
    const { tree, ignoredIds } = removeAutoIgnoreNarrativeChildren(
      narrativeTree,
      rep.documents,
      syncSettings,
      manualIgnoreIds
    );
    // Replace our subtree in rep
    autoIgnoredRep = autoIgnoredRep.update("documentTree", documentTree => replaceSubTree(documentTree, tree));
    // Add ignored IDs into our list
    allIgnoredIds.push(...ignoredIds);
  }

  const autoIgnoredIds = Set(allIgnoredIds);

  // Strip out the documents that we are ignoring
  autoIgnoredRep = autoIgnoredRep.update("documents", documents =>
    documents.filter(doc => !autoIgnoredIds.has(doc.id))
  );

  return {
    rep: autoIgnoredRep,
    autoIgnoredIds
  };
};
